import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector, TypedUseSelectorHook } from "react-redux";
import authReducer from "./slices/authSlice";
import janusPublishersReducer from "./slices/janusPublishersSlice";
import notificationsReducer from "./slices/notificationsSlice";
import basketReducer from "./slices/basketSlice";
import uiLanguagesReducer from "./slices/uiLanguagesSlice";
import { assignmentApi } from "../api/assignment/assignmentApi";
import { authPrivateApi } from "@/api/auth/authPrivateApi";
import { authPublicApi } from "@/api/auth/authPublicApi";
import { classApi } from "@/api/class/classApi";
import { quizApi } from "@/api/quiz/quizApi";
import { enumerationApi } from "@/api/enums/enumsApi";
import { reportApi } from "@/api/reports/reportApi";
import { publicReportApi } from "@/api/reports/publicReportApi";
import { settingsProfilesApi } from "@/api/proctoring/settingsProfilesApi";
import { inviteCodeApi } from "../api/invite-code/inviteCodeApi";
import { inviteCodePublicApi } from "@/api/invite-code/inviteCodePublicApi";
import { registerBySlugPublicApi } from "@/api/register/registerBySlugPublicApi";
import { productApi } from "../api/product/productApi";
import { userApi } from "@/api/user/userApi";
import { currenciesApi } from "@/api/currencies/currenciesApi";
import { certificatesApi } from "@/api/certificates/certificatesApi";
import { organizationsApi } from "@/api/organizations/organizationsApi";
import { mediaApi } from "@/api/media/mediaApi";
import { codeExecutionApi } from "@/api/code-execution/codeExecutionApi";
import { chatsApi } from "@/api/chats/chatsApi";
import { paymentProvidersApi } from "@/api/payment-providers/paymentProvidersApi";
import { basketApi } from "@/api/basket/basketApi";
import { billingApi } from "@/api/billing/billingApi";
import { managerApi } from "@/api/manager/managerApi";
import { departmentApi } from "@/api/department/departmentApi";
import { analyticsApi } from "@/api/analytics/analyticsApi";
import { languagesApi } from "@/api/languages/languagesApi";
import { reviewerReportsApi } from "@/api/reviewer-reports/reviewerReportsApi";

export const store = configureStore({
  reducer: {
    auth: authReducer,
    janusPublishers: janusPublishersReducer,
    notifications: notificationsReducer,
    basket: basketReducer,
    uiLanguages: uiLanguagesReducer,
    [assignmentApi.reducerPath]: assignmentApi.reducer,
    [authPrivateApi.reducerPath]: authPrivateApi.reducer,
    [authPublicApi.reducerPath]: authPublicApi.reducer,
    [classApi.reducerPath]: classApi.reducer,
    [quizApi.reducerPath]: quizApi.reducer,
    [enumerationApi.reducerPath]: enumerationApi.reducer,
    [reportApi.reducerPath]: reportApi.reducer,
    [publicReportApi.reducerPath]: publicReportApi.reducer,
    [settingsProfilesApi.reducerPath]: settingsProfilesApi.reducer,
    [inviteCodeApi.reducerPath]: inviteCodeApi.reducer,
    [inviteCodePublicApi.reducerPath]: inviteCodePublicApi.reducer,
    [registerBySlugPublicApi.reducerPath]: registerBySlugPublicApi.reducer,
    [productApi.reducerPath]: productApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [currenciesApi.reducerPath]: currenciesApi.reducer,
    [certificatesApi.reducerPath]: certificatesApi.reducer,
    [organizationsApi.reducerPath]: organizationsApi.reducer,
    [mediaApi.reducerPath]: mediaApi.reducer,
    [codeExecutionApi.reducerPath]: codeExecutionApi.reducer,
    [chatsApi.reducerPath]: chatsApi.reducer,
    [paymentProvidersApi.reducerPath]: paymentProvidersApi.reducer,
    [basketApi.reducerPath]: basketApi.reducer,
    [billingApi.reducerPath]: billingApi.reducer,
    [managerApi.reducerPath]: managerApi.reducer,
    [departmentApi.reducerPath]: departmentApi.reducer,
    [analyticsApi.reducerPath]: analyticsApi.reducer,
    [languagesApi.reducerPath]: languagesApi.reducer,
    [reviewerReportsApi.reducerPath]: reviewerReportsApi.reducer,
  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        /**
         * RTK Query stores mutation results under `${api.reducerPath}.mutations.*.data`.
         * Our export endpoints intentionally return `Blob`, which is not serializable.
         * Ignore these branches to avoid console warnings.
         */
        ignoredPaths: [
          "assignmentApi.mutations",
          "certificatesApi.mutations",
        ],

        /**
         * RTK Query export endpoints dispatch fulfilled actions whose `payload` is a Blob.
         * Ignore these specific action types to avoid non-serializable action warnings.
         */
        ignoredActions: [
          "assignmentApi/executeMutation/fulfilled",
          "assignmentApi/executeMutation/rejected",
          "certificatesApi/executeMutation/fulfilled",
          "certificatesApi/executeMutation/rejected",
        ],
      },
    })
      .concat(assignmentApi.middleware)
      .concat(enumerationApi.middleware)
      .concat(authPublicApi.middleware)
      .concat(authPrivateApi.middleware)
      .concat(classApi.middleware)
      .concat(reportApi.middleware)
      .concat(publicReportApi.middleware)
      .concat(quizApi.middleware)
      .concat(settingsProfilesApi.middleware)
      .concat(inviteCodeApi.middleware)
      .concat(inviteCodePublicApi.middleware)
      .concat(registerBySlugPublicApi.middleware)
      .concat(userApi.middleware)
      .concat(currenciesApi.middleware)
      .concat(certificatesApi.middleware)
      .concat(organizationsApi.middleware)
      .concat(mediaApi.middleware)
      .concat(codeExecutionApi.middleware)
      .concat(productApi.middleware)
      .concat(chatsApi.middleware)
      .concat(paymentProvidersApi.middleware)
      .concat(basketApi.middleware)
      .concat(billingApi.middleware)
      .concat(managerApi.middleware)
      .concat(departmentApi.middleware)
      .concat(analyticsApi.middleware)
      .concat(languagesApi.middleware)
      .concat(reviewerReportsApi.middleware),
  devTools: process.env.NODE_ENV !== "production",
});

// Типы
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
